import { motion, useInView, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import { Link } from "@tanstack/react-router";
import { Navbar, Footer } from "@/components/site-chrome";
import { Button } from "@/components/ui/button";
import { ArrowRightIcon } from "@/components/ui/TriIcons";

const EASE = [0.16, 1, 0.3, 1] as const;
const FOREST = "#0A4A3E";
const FOREST_MID = "#6FAE68";
const FOREST_DEEP = "#052E27";
const GOLD = "#E7B84B";
const INK = "#123D35";
const PAPER = "#FCFDF8";
const PAPER2 = "#F2F8EE";
const LINE = "#D3E0CC";
const MUTED = "#60756C";
const AQUA = "#BFE5D7";
const LEAF = "#8FBE72";
const MINT = "#BFE5D7";
const WHITE = "#FFFFFF";
const FS = "'Montserrat', system-ui, sans-serif";

const WEEKS = [
  { week: 1, title: "Map your school", desc: "We document every department, workflow and data source.", detail: "We sit with your teams and map the real workflows behind fees, admissions, attendance, transport, boarding, procurement, communication and reporting.", color: FOREST, icon: "map" },
  { week: 2, title: "Move your data", desc: "Students, staff, fees and records migrated securely.", detail: "Student records, staff, classes, fee balances, transactions and relevant historical data are securely structured and migrated into Fundred.", color: GOLD, icon: "data" },
  { week: 3, title: "Activate your people", desc: "Training for administrators, teachers and parents.", detail: "Administrators, finance teams, teachers, transport teams and parents are introduced to the tools they actually need. Training happens around real institutional workflows.", color: FOREST_MID, icon: "people" },
  { week: 4, title: "Go live", desc: "The school starts running on Fundred — fully connected.", detail: "Payments start flowing. Attendance starts capturing. Buses become visible. Communication becomes connected. Reports begin generating automatically.", color: MINT, icon: "live" },
  { week: 5, title: "Optimise", desc: "Fine-tune workflows, reports and intelligence alerts.", detail: "We watch usage, identify friction, review feedback and fine-tune workflows, dashboards, permissions and automation.", color: GOLD, icon: "tune" },
];

const TRANSFORMATION = [
  { label: "PAPER", sub: "Fragmented", color: MUTED },
  { label: "DATA", sub: "Structured", color: FOREST },
  { label: "PEOPLE", sub: "Onboarded", color: FOREST_MID },
  { label: "LIVE", sub: "Operating", color: GOLD },
  { label: "INTELLIGENCE", sub: "Optimised", color: LEAF },
];

const iconPaths: Record<string, string> = {
  map: "M9 20l-5.5-2.5L9 15l6 2.5L9 20zM9 15V5l6 2.5v10L9 15zM15 17.5V7.5L20 5v10l-5 2.5z",
  data: "M3 12l3-9 6 3-3 9-6-3zM12 15l3-9 6 3-3 9-6-3z",
  people: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75",
  live: "M5 3v18l7-5 7 5V3zM5 3h14",
  tune: "M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83",
};

function WeekIcon({ type, size = 24, color = "currentColor" }: { type: string; size?: number; color?: string }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d={iconPaths[type] ?? iconPaths.map} />
    </svg>
  );
}

export function FiveWeeksPage() {
  return (
    <div className="relative min-h-screen overflow-hidden" style={{ background: PAPER, fontFamily: FS }}>
      <div className="absolute top-0 left-0 right-0 z-50 h-[3px]" style={{ background: `linear-gradient(90deg, ${FOREST}, ${GOLD}, ${FOREST_MID}, ${MINT}, ${GOLD})` }} />
      <div className="relative z-10">
        <Navbar />
        <FiveWeeksHero />
        <TimelineSection />
        <TransformationSection />
        <PromiseSection />
        <CTASection />
      </div>
      <Footer />
    </div>
  );
}

function FiveWeeksHero() {
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 0.5], [0, -30]);
  const opacityText = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const [activePhase, setActivePhase] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setActivePhase(p => (p + 1) % 5), 2000);
    return () => clearInterval(id);
  }, []);

  const phases = [
    { label: "SURVEY", color: FOREST },
    { label: "MIGRATE", color: GOLD },
    { label: "ACTIVATE", color: FOREST_MID },
    { label: "LAUNCH", color: MINT },
    { label: "OPTIMISE", color: LEAF },
  ];

  return (
    <section ref={ref} className="relative overflow-hidden px-4 pb-12 pt-28 sm:px-8 sm:pt-44 sm:pb-24" style={{ background: PAPER }}>
      {/* Architectural blueprint grid */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{ backgroundImage: `linear-gradient(${INK}55 1px, transparent 1px), linear-gradient(90deg, ${INK}55 1px, transparent 1px)`, backgroundSize: "48px 48px" }} />

      {/* Blueprint annotations */}
      <svg className="pointer-events-none absolute inset-0 h-full w-full" preserveAspectRatio="xMidYMid slice" viewBox="0 0 1200 800">
        {/* Dimension lines */}
        <g stroke={FOREST} strokeWidth="0.5" opacity="0.15" fill="none">
          <line x1="100" y1="100" x2="100" y2="700" />
          <line x1="80" y1="100" x2="120" y2="100" />
          <line x1="80" y1="700" x2="120" y2="700" />
          <line x1="100" y1="400" x2="1100" y2="400" strokeDasharray="4 8" />
          <line x1="1100" y1="380" x2="1100" y2="420" />
          <text x="60" y="404" fill={FOREST} fontSize="10" opacity="0.25" style={{ fontFamily: "monospace" }}>35W</text>
        </g>
        {/* Foundation blocks */}
        <g stroke={FOREST} strokeWidth="0.5" opacity="0.12" fill="none">
          <rect x="150" y="450" width="200" height="120" />
          <rect x="400" y="450" width="200" height="120" strokeDasharray="6 4" />
          <rect x="650" y="450" width="200" height="120" />
          <rect x="900" y="450" width="150" height="120" strokeDasharray="6 4" />
          <text x="170" y="475" fill={FOREST} fontSize="9" opacity="0.2" style={{ fontFamily: "monospace" }}>WK.01</text>
          <text x="420" y="475" fill={FOREST} fontSize="9" opacity="0.2" style={{ fontFamily: "monospace" }}>WK.02</text>
          <text x="670" y="475" fill={FOREST} fontSize="9" opacity="0.2" style={{ fontFamily: "monospace" }}>WK.03</text>
          <text x="920" y="475" fill={FOREST} fontSize="9" opacity="0.2" style={{ fontFamily: "monospace" }}>WK.04-05</text>
        </g>
        {/* Animated construction beam */}
        <motion.line
          x1="150" y1="300" x2="1050" y2="300"
          stroke={GOLD} strokeWidth="1.5" opacity="0.3"
          strokeDasharray="2 6"
          animate={{ strokeDashoffset: [0, -16, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        />
        {/* Phase indicator dots */}
        {[150, 400, 650, 900, 1050].map((x, i) => (
          <motion.circle key={i} cx={x} cy="300" r="5" fill={phases[i].color} animate={{ opacity: activePhase === i ? 1 : 0.2, r: activePhase === i ? 7 : 5 }} transition={{ duration: 0.3 }} />
        ))}
      </svg>

      {/* Ambient orbs */}
      <motion.div animate={{ x: [0, 20, 0], y: [0, -15, 0] }} transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }} className="pointer-events-none absolute left-10 top-20 h-40 w-40 rounded-full opacity-30 blur-3xl" style={{ background: FOREST_MID }} />
      <motion.div animate={{ x: [0, -15, 0], y: [0, 10, 0] }} transition={{ duration: 14, repeat: Infinity, ease: "easeInOut", delay: 1 }} className="pointer-events-none absolute right-10 bottom-10 h-48 w-48 rounded-full opacity-20 blur-3xl" style={{ background: GOLD }} />

      <motion.div style={{ y: yText, opacity: opacityText }} className="relative mx-auto max-w-6xl px-4">
        {/* Blueprint badge */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }} className="mb-3 inline-flex items-center gap-2 rounded-full border px-3 py-1" style={{ borderColor: `${FOREST_MID}25`, background: `${FOREST_MID}08` }}>
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: FOREST_MID }} />
          <span className="text-[9px] font-bold uppercase tracking-[0.2em]" style={{ color: FOREST_MID }}>Implementation Blueprint</span>
        </motion.div>

        {/* Heading with blueprint-style number */}
        <div className="relative">
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 0.06 }} transition={{ delay: 0.3, duration: 1 }} className="pointer-events-none absolute -top-8 -left-4 text-[120px] font-black leading-none" style={{ color: FOREST, fontFamily: "monospace" }}>05</motion.span>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }} className="relative text-3xl font-bold tracking-tight sm:text-6xl" style={{ color: INK, letterSpacing: "-0.04em", lineHeight: 0.92 }}>
            From paperwork to platform in
          </motion.h1>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.6, ease: EASE }} className="relative text-3xl font-bold tracking-tight sm:text-6xl" style={{ color: FOREST_MID, letterSpacing: "-0.04em", lineHeight: 0.92 }}>
            five <span style={{ color: GOLD, fontStyle: "italic" }}>weeks.</span>
          </motion.h1>
        </div>

        {/* Phase progress bar */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 0.6 }} className="mt-8 max-w-md">
          <div className="flex items-center justify-between">
            {phases.map((p, i) => (
              <div key={p.label} className="flex flex-col items-center gap-1.5">
                <motion.div className="h-3 w-3 rounded-full" style={{ background: activePhase === i ? p.color : `${p.color}30` }} animate={{ scale: activePhase === i ? 1.3 : 1 }} transition={{ duration: 0.3 }} />
                <span className="text-[8px] font-mono font-bold uppercase tracking-wider" style={{ color: activePhase === i ? p.color : `${INK}30` }}>{p.label}</span>
              </div>
            ))}
          </div>
          <div className="mt-2 h-0.5 w-full rounded-full" style={{ background: `${LINE}80` }}>
            <motion.div className="h-full rounded-full" style={{ background: `linear-gradient(90deg, ${FOREST}, ${GOLD}, ${FOREST_MID}, ${MINT}, ${LEAF})` }} animate={{ width: `${((activePhase + 1) / 5) * 100}%` }} transition={{ duration: 0.4, ease: EASE }} />
          </div>
        </motion.div>

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.6 }} className="mt-6 max-w-xl text-sm leading-relaxed sm:text-lg" style={{ color: MUTED }}>
          No six-month IT project. No army of consultants. No ripping your school apart.
        </motion.p>
      </motion.div>
    </section>
  );
}

function TimelineSection() {
  const [activeWeek, setActiveWeek] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });

  useEffect(() => {
    if (!autoPlay) return;
    const id = setInterval(() => setActiveWeek(w => (w + 1) % WEEKS.length), 3000);
    return () => clearInterval(id);
  }, [autoPlay]);

  const week = WEEKS[activeWeek];

  return (
    <section ref={ref} className="relative py-16 sm:py-24" style={{ background: FOREST_DEEP }}>
      <div className="pointer-events-none absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `linear-gradient(${FOREST_MID} 1px, transparent 1px), linear-gradient(90deg, ${FOREST_MID} 1px, transparent 1px)`, backgroundSize: "40px 40px" }} />
      {/* Ambient glow following active week */}
      <motion.div
        className="pointer-events-none absolute inset-0"
        animate={{ background: `radial-gradient(ellipse 40% 30% at ${10 + activeWeek * 20}% 30%, ${week.color}12 0%, transparent 50%)` }}
        transition={{ duration: 0.6 }}
      />
      <div className="relative mx-auto max-w-6xl px-4 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, ease: EASE }}
          className="mb-3 inline-flex items-center gap-2 rounded-full border px-3 py-1"
          style={{ borderColor: "rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)" }}
        >
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: GOLD }} />
          <span className="text-[9px] font-bold uppercase tracking-[0.2em]" style={{ color: "rgba(255,255,255,0.7)" }}>The Journey</span>
        </motion.div>

        {/* Desktop: horizontal timeline */}
        <div className="mt-10 hidden lg:block">
          <div className="relative mb-8">
            <div className="absolute left-[10%] right-[10%] top-8 h-0.5 rounded-full" style={{ background: "rgba(255,255,255,0.08)" }} />
            <motion.div
              className="absolute left-[10%] top-8 h-0.5 rounded-full"
              style={{ background: `linear-gradient(90deg, ${FOREST}, ${GOLD}, ${FOREST_MID}, ${MINT}, ${GOLD})` }}
              animate={{ width: `${(activeWeek / (WEEKS.length - 1)) * 80}%` }}
              transition={{ duration: 0.5, ease: EASE }}
            />
            <div className="relative flex justify-between">
              {WEEKS.map((w, i) => {
                const isActive = i === activeWeek;
                const isPast = i < activeWeek;
                return (
                  <button key={w.week} onClick={() => { setActiveWeek(i); setAutoPlay(false); }} className="group relative flex flex-col items-center gap-3">
                    <motion.div
                      className="grid h-16 w-16 place-items-center rounded-full border-2 transition-all"
                      style={{ borderColor: isActive ? w.color : isPast ? `${w.color}60` : "rgba(255,255,255,0.1)", background: isActive ? w.color : isPast ? `${w.color}20` : "rgba(255,255,255,0.03)" }}
                      animate={{ scale: isActive ? 1.15 : 1 }}
                      whileHover={{ scale: 1.1 }}
                    >
                      <WeekIcon type={w.icon} size={24} color={isActive ? "#fff" : isPast ? w.color : "rgba(255,255,255,0.3)"} />
                    </motion.div>
                    <span className="absolute -top-1 -right-1 grid h-6 w-6 place-items-center rounded-full text-[10px] font-bold" style={{ background: isActive ? w.color : "rgba(255,255,255,0.08)", color: isActive ? "#fff" : "rgba(255,255,255,0.4)" }}>{w.week}</span>
                    <div className="text-center">
                      <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: isActive ? w.color : "rgba(255,255,255,0.3)" }}>Week {w.week}</p>
                      <p className="mt-1 text-sm font-bold" style={{ color: isActive ? "#fff" : "rgba(255,255,255,0.4)" }}>{w.title.split(" ")[0]}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Active week detail */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeWeek}
              initial={{ opacity: 0, y: 16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.98 }}
              transition={{ duration: 0.3, ease: EASE }}
              className="relative mt-8 overflow-hidden rounded-2xl border p-6"
              style={{ borderColor: `${week.color}30`, background: `${week.color}08` }}
            >
              <div className="absolute top-0 left-0 right-0 h-1" style={{ background: week.color }} />
              <div className="flex items-center gap-4">
                <motion.div
                  className="grid h-14 w-14 place-items-center rounded-2xl"
                  style={{ background: `${week.color}20` }}
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <WeekIcon type={week.icon} size={28} color={week.color} />
                </motion.div>
                <div>
                  <p className="text-xl font-bold" style={{ color: "#fff" }}>{week.title}</p>
                  <p className="text-sm" style={{ color: week.color }}>Week {week.week}</p>
                </div>
              </div>
              <p className="mt-4 text-base font-medium" style={{ color: "rgba(255,255,255,0.8)" }}>{week.desc}</p>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.5)" }}>{week.detail}</p>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Mobile: vertical timeline */}
        <div className="mt-8 lg:hidden">
          <div className="relative">
            <div className="absolute bottom-4 left-[27px] top-4 w-0.5 rounded-full" style={{ background: `linear-gradient(180deg, ${WEEKS.map(w => w.color).join(", ")})` }} />
            <div className="space-y-4">
              {WEEKS.map((w, i) => {
                const isActive = i === activeWeek;
                return (
                  <button key={w.week} onClick={() => { setActiveWeek(i); setAutoPlay(false); }} className="relative flex w-full gap-4 text-left">
                    <motion.div className="relative z-10 grid h-14 w-14 shrink-0 place-items-center rounded-full border-2" style={{ borderColor: isActive ? w.color : `${w.color}40`, background: isActive ? w.color : `${w.color}10` }} animate={{ scale: isActive ? 1.05 : 1 }}>
                      <WeekIcon type={w.icon} size={20} color={isActive ? "#fff" : w.color} />
                      <span className="absolute -top-1 -right-1 grid h-5 w-5 place-items-center rounded-full text-[9px] font-bold" style={{ background: w.color, color: "#fff" }}>{w.week}</span>
                    </motion.div>
                    <div className="flex-1 rounded-xl border p-4" style={{ borderColor: isActive ? `${w.color}30` : "rgba(255,255,255,0.06)", background: isActive ? `${w.color}08` : "rgba(255,255,255,0.02)" }}>
                      <div className="flex items-baseline justify-between">
                        <h3 className="text-lg font-bold" style={{ color: "#fff" }}>{w.title}</h3>
                        <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: w.color }}>Week {w.week}</span>
                      </div>
                      <p className="mt-1 text-xs leading-snug" style={{ color: "rgba(255,255,255,0.6)" }}>{w.desc}</p>
                      {isActive && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mt-3 overflow-hidden">
                          <p className="text-[11px] leading-snug" style={{ color: "rgba(255,255,255,0.5)" }}>{w.detail}</p>
                        </motion.div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-center gap-3">
          <button
            onClick={() => setAutoPlay(!autoPlay)}
            className="flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-medium transition-all hover:scale-105"
            style={{ borderColor: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.6)" }}
          >
            <motion.span animate={{ scale: autoPlay ? [1, 1.3, 1] : 1 }} transition={{ duration: 1.5, repeat: autoPlay ? Infinity : 0 }} className="h-2 w-2 rounded-full" style={{ background: autoPlay ? GOLD : "rgba(255,255,255,0.2)" }} />
            {autoPlay ? "Auto-playing" : "Paused — click weeks to explore"}
          </button>
        </div>
      </div>
    </section>
  );
}

function TransformationSection() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <section ref={ref} className="relative overflow-hidden py-16 sm:py-24" style={{ background: FOREST }}>
      <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(ellipse 60% 40% at 30% 20%, ${FOREST_MID}18 0%, transparent 50%), radial-gradient(ellipse 50% 40% at 70% 80%, ${GOLD}10 0%, transparent 50%)` }} />
      <div className="relative mx-auto max-w-5xl px-4 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, ease: EASE }}
          className="mb-8 text-center sm:mb-12"
        >
          <span className="text-[9px] font-bold uppercase tracking-[0.2em]" style={{ color: GOLD }}>The Transformation</span>
          <h2 className="mt-3 text-2xl font-bold tracking-tight sm:text-4xl" style={{ color: "#fff", letterSpacing: "-0.04em", lineHeight: 0.95 }}>Five stages. One continuous shift.</h2>
        </motion.div>

        {/* Mobile: horizontal scroll */}
        <div className="-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-4 lg:hidden [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: "none" }}>
          {TRANSFORMATION.map((t, i) => (
            <motion.div
              key={t.label}
              initial={{ opacity: 0, scale: 0.85 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: i * 0.1, duration: 0.4 }}
              className="flex shrink-0 snap-center flex-col items-center gap-2"
              style={{ width: "140px" }}
            >
              <div className="relative w-full overflow-hidden rounded-xl border p-4 text-center" style={{ borderColor: `${t.color}40`, background: `${t.color}12` }}>
                <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: t.color }} />
                <p className="text-base font-bold tracking-tight" style={{ color: t.color }}>{t.label}</p>
                <p className="mt-1 text-[9px] uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.4)" }}>{t.sub}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Desktop: animated bar */}
        <div className="hidden items-center lg:flex">
          {TRANSFORMATION.map((t, i) => (
            <div key={t.label} className="flex flex-1 items-center gap-3">
              <motion.div
                initial={{ opacity: 0, scale: 0.85 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: i * 0.12, duration: 0.5, ease: EASE }}
                whileHover={{ y: -4, scale: 1.05 }}
                className="group relative flex-1 overflow-hidden rounded-xl border p-5 text-center"
                style={{ borderColor: `${t.color}40`, background: `${t.color}12` }}
              >
                <div className="absolute inset-0 opacity-0 transition-opacity duration-400 group-hover:opacity-100" style={{ background: `radial-gradient(circle at 50% 50%, ${t.color}15 0%, transparent 70%)` }} />
                <div className="relative">
                  <p className="text-lg font-bold tracking-tight" style={{ color: t.color }}>{t.label}</p>
                  <p className="mt-1 text-[10px] uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.4)" }}>{t.sub}</p>
                </div>
                <div className="absolute bottom-0 left-0 h-0.5 w-0 transition-all duration-500 group-hover:w-full" style={{ background: t.color }} />
              </motion.div>
              {i < TRANSFORMATION.length - 1 && (
                <motion.div initial={{ opacity: 0, x: -10 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ delay: i * 0.12 + 0.1, duration: 0.4 }}>
                  <ArrowRightIcon className="h-5 w-5 shrink-0" accent="rgba(255,255,255,0.3)" style={{ color: "rgba(255,255,255,0.3)" }} />
                </motion.div>
              )}
            </div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="mt-12 text-center sm:mt-16"
        >
          <p className="text-xl leading-relaxed sm:text-3xl" style={{ color: "rgba(255,255,255,0.7)" }}>Paperwork doesn't get digitised.</p>
          <p className="text-2xl font-bold sm:text-4xl" style={{ color: GOLD, fontStyle: "italic" }}>The institution gets transformed.</p>
        </motion.div>
      </div>
    </section>
  );
}

function PromiseSection() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });

  const promises = [
    { title: "No six-month IT project", desc: "Five focused weeks. Measured, deliberate, and done.", color: FOREST_MID },
    { title: "No army of consultants", desc: "Our team handles the complexity. Your team keeps operating.", color: GOLD },
    { title: "No ripping your school apart", desc: "Your institution runs throughout the transition.", color: FOREST },
  ];

  return (
    <section ref={ref} className="relative py-16 sm:py-24" style={{ background: PAPER }}>
      <div className="relative mx-auto max-w-5xl px-4 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, ease: EASE }}
        >
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border px-3 py-1" style={{ borderColor: `${FOREST}20`, background: `${FOREST}06` }}>
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: FOREST }} />
            <span className="text-[9px] font-bold uppercase tracking-[0.2em]" style={{ color: FOREST }}>The Promise</span>
          </div>
          <h2 className="text-2xl font-bold tracking-tight sm:text-4xl" style={{ color: INK, letterSpacing: "-0.04em", lineHeight: 1.05 }}>
            What it <span style={{ color: FOREST_MID, fontStyle: "italic" }}>isn't.</span>
          </h2>
        </motion.div>

        <div className="mt-10 grid gap-4 sm:grid-cols-3">
          {promises.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.1, duration: 0.5, ease: EASE }}
              whileHover={{ y: -6 }}
              className="group relative overflow-hidden rounded-2xl border bg-white p-6"
              style={{ borderColor: `${p.color}20`, boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}
            >
              <div className="absolute inset-0 opacity-0 transition-opacity duration-400 group-hover:opacity-100" style={{ background: `linear-gradient(135deg, ${p.color}06 0%, transparent 60%)` }} />
              <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: p.color }} />
              <div className="relative">
                <div className="mb-4 flex items-center gap-3">
                  <span className="text-4xl font-black opacity-[0.08]" style={{ color: p.color }}>0{i + 1}</span>
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: `${p.color}10` }}>
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke={p.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-base font-bold" style={{ color: INK }}>{p.title}</h3>
                <p className="mt-2 text-xs leading-relaxed" style={{ color: MUTED }}>{p.desc}</p>
              </div>
              <div className="absolute bottom-0 left-0 h-0.5 w-0 transition-all duration-500 group-hover:w-full" style={{ background: p.color }} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="relative overflow-hidden py-16 sm:py-24" style={{ background: FOREST }}>
      <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(ellipse 50% 40% at 50% 50%, ${GOLD}08 0%, transparent 60%)` }} />
      {[...Array(8)].map((_, i) => (
        <motion.div
          key={i}
          className="pointer-events-none absolute h-1 w-1 rounded-full"
          style={{ background: GOLD, left: `${10 + i * 11}%`, top: `${15 + (i % 4) * 22}%` }}
          animate={{ y: [0, -25, 0], opacity: [0, 0.5, 0] }}
          transition={{ duration: 5, repeat: Infinity, delay: i * 0.4, ease: "easeInOut" }}
        />
      ))}
      <div className="relative mx-auto max-w-3xl px-6 text-center">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease: EASE }}>
          <h2 className="text-3xl font-bold tracking-tight sm:text-5xl" style={{ color: "#fff", letterSpacing: "-0.04em", lineHeight: 0.92 }}>
            Ready to see your
          </h2>
          <h2 className="text-3xl font-bold tracking-tight sm:text-5xl" style={{ color: GOLD, letterSpacing: "-0.04em", lineHeight: 0.92, fontStyle: "italic" }}>
            entire school?
          </h2>
          <p className="mt-4 text-sm sm:text-lg" style={{ color: "rgba(255,255,255,0.5)" }}>Book a 20-minute walkthrough with our team.</p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button asChild size="lg" className="h-12 rounded-full px-8 text-[13px] font-bold uppercase tracking-[0.15em] hover:opacity-90" style={{ background: GOLD, color: INK, border: "none" }}>
              <Link to="/auth">Get started <ArrowRightIcon className="ml-2 h-4 w-4" accent={INK} style={{ color: INK }} /></Link>
            </Button>
            <Button asChild variant="ghost" size="lg" className="h-12 rounded-full px-7 text-[13px] font-bold uppercase tracking-[0.15em] text-white" style={{ border: `1.5px solid ${GOLD}50` }}>
              <Link to="/experience">Explore the Experience</Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default FiveWeeksPage;
